/**
 * Promoters Tool
 *
 * Tool implementation for Promoter Visits tracking
 */

import { FaMapMarkerAlt } from "react-icons/fa";
import { BaseTool } from "../BaseTool";
import { promotersApi } from "../../services/toolsApi";

export class PromotersTool extends BaseTool {
  constructor() {
    super();

    // Metadata
    this.id = "promoter-visits";
    this.name = "Promoter Visits";
    this.description = "Promoter visit tracking and session analytics";
    this.icon = FaMapMarkerAlt;
    this.color = "#10B981";
    this.path = "/dashboard/tools/promoters?tab=visits";
    this.navHidden = true; // reached via the Promoters tool's Visits tab

    // Collections
    this.collections = [
      "promoter_visits",
      "promoter_daily_stats",
      "promoter_daily_referral_stats",
      "promoter_referral_stats",
      "promoter_visit_stats",
    ];

    // Access control
    this.requiresAdmin = false;

    // Summary configuration
    this.summaryConfig = {
      enabled: true,
      mainStat: "todayVisits",
      expectedStatCount: 4, // todayVisits, totalVisits, weekVisits, activeCompanies
    };

    // Filter configuration
    this.filterConfig = {
      search: {
        enabled: true,
        placeholder: "Search companies...",
        fields: ["companyName", "referralKey"],
      },
      buttonGroups: [
        {
          label: "Time Period",
          key: "timeFilter",
          defaultValue: "all",
          options: [
            { value: "all", label: "All Time", icon: "FaClock" },
            { value: "today", label: "Today", icon: "FaCalendarDay" },
            { value: "week", label: "This Week", icon: "FaCalendarWeek" },
          ],
        },
      ],
      sortOptions: [
        { value: "recent", label: "Most Recent" },
        { value: "visits", label: "Most Visits" },
        { value: "name", label: "Company Name" },
      ],
      defaultSort: "recent",
    };
  }

  /**
   * Get summary data for dashboard widget
   */
  async getSummaryData() {
    try {
      // Served by the promoterStats Cloud Function (reads the pre-aggregated
      // promoter_*_stats collections). Event-based counts.
      const response = await promotersApi.getStats();
      const summary = response?.summary || {};

      return {
        success: true,
        data: {
          todayVisits: summary.today || 0,
          totalVisits: summary.total || 0,
          weekVisits: summary.week || 0,
          activeCompanies: summary.companies || 0,
        },
        lastUpdated: response.lastUpdated
          ? new Date(response.lastUpdated)
          : new Date(),
      };
    } catch (error) {
      console.error("Error fetching promoter summary:", error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  /**
   * Custom backup preparation
   * Could aggregate sessions or filter old visits
   */
  async prepareBackup(collections, format) {
    // Could add logic to:
    // - Aggregate page views into sessions
    // - Exclude visits older than X days
    // - Anonymize company data if needed

    return collections;
  }
}

export default PromotersTool;
